import { useMemo } from 'react';

import { ChartCard } from '@/components/ChartCard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Skeleton } from '@/components/ui/skeleton';

import { useFetch } from 'hooks/useFetch';

import { type IPlotlyElement } from 'client-types/';

interface Props {
  element: IPlotlyElement;
}

const _PlotlyElement = ({ element }: Props) => {
  const { data, error, isLoading } = useFetch(element.url || null);

  // deep-clone SWR data so Plotly.js mutations don't corrupt the cache.
  // keyed on the data reference so clones stay stable between re-renders,
  // preserving react-plotly.js's prevProps === this.props skip check.
  const plotly = useMemo(() => {
    if (!data) return null;
    return {
      data: structuredClone(data.data),
      layout: structuredClone(data.layout),
      frames: data.frames ? structuredClone(data.frames) : undefined,
      config: data.config ? structuredClone(data.config) : undefined,
      height: data.layout?.height || 400
    };
  }, [data]);

  // A skeleton at the chart's own height, not a bare "Loading..." that the
  // layout then jumps past when the figure arrives.
  if (isLoading)
    return <Skeleton className="w-full rounded-xl" style={{ height: 236 }} />;
  if (error) return <div>An error occurred</div>;
  if (!plotly) return null;

  return (
    <ChartCard
      className={`${element.display}-plotly`}
      data={plotly.data}
      layout={plotly.layout}
      frames={plotly.frames}
      config={plotly.config}
    />
  );
};

const PlotlyElement = (props: Props) => {
  return (
    <ErrorBoundary prefix="Failed to load chart.">
      <_PlotlyElement {...props} />
    </ErrorBoundary>
  );
};

export { PlotlyElement };
